import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit2, Eye, Archive, RotateCcw, Copy, Mail, MessageSquare } from 'lucide-react';
import { getBrandName } from '@/lib/brandName';

const CHANNELS = ['SMS', 'EMAIL'];
const TEMPLATE_TYPES = ['SHIFT_REMINDER', 'LEAVE_APPROVAL', 'DOCUMENT_REQUEST', 'ONBOARDING_INVITE', 'INVOICE_SENT', 'CLAIM_ALERT', 'REFERRAL_CONFIRMATION', 'GENERAL'];

function TemplateForm({ template, onSave, onCancel }) {
  const [form, setForm] = useState(template || { name: '', channel: 'SMS', templateType: 'GENERAL', subject: '', body: '', active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (template?.id) {
        await api.put(`/communications/templates/${template.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/communications/templates', form);
        toast.success('Template created');
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to save template');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div><Label>Name</Label><Input data-testid="template-name-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Template name" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Channel</Label>
          <Select value={form.channel} onValueChange={v => set('channel', v)}>
            <SelectTrigger data-testid="template-channel-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={form.templateType} onValueChange={v => set('templateType', v)}>
            <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {form.channel === 'EMAIL' && <div><Label>Subject</Label><Input data-testid="template-subject-input" value={form.subject || ''} onChange={e => set('subject', e.target.value)} placeholder="Email subject with {{merge_fields}}" /></div>}
      <div><Label>Body</Label><Textarea data-testid="template-body-input" value={form.body} onChange={e => set('body', e.target.value)} placeholder="Template body with {{merge_fields}}" rows={6} /></div>
      <p className="text-xs text-muted-foreground">Use {"{{field_name}}"} for merge fields, e.g. {"{{recipient_name}}"}, {"{{shift_date}}"}</p>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="template-save-btn" onClick={handleSave} disabled={saving || !form.name || !form.body}>{saving ? 'Saving...' : template?.id ? 'Update' : 'Create'}</Button>
      </DialogFooter>
    </div>
  );
}

function PreviewDialog({ templateId, open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [mergeData, setMergeData] = useState({ recipient_name: 'Jane Doe', shift_date: '2026-03-15', organisation_name: getBrandName() });

  const loadPreview = useCallback(async () => {
    if (!templateId) return;
    try {
      const { data } = await api.post(`/communications/templates/${templateId}/preview`, mergeData);
      setPreview(data.data);
    } catch { toast.error('Failed to load preview'); }
  }, [templateId, mergeData]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Template Preview</DialogTitle></DialogHeader>
        {preview && (
          <div className="space-y-3">
            <Badge>{preview.channel}</Badge>
            {preview.subject && <div><Label className="text-xs text-muted-foreground">Subject</Label><p className="font-medium">{preview.subject}</p></div>}
            <div><Label className="text-xs text-muted-foreground">Body</Label><div className="p-3 rounded bg-muted text-sm whitespace-pre-wrap">{preview.body}</div></div>
            {preview.unresolvedFields?.length > 0 && <p className="text-xs text-amber-600">Unresolved fields: {preview.unresolvedFields.join(', ')}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CommunicationTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editTemplate, setEditTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', pageSize: '50' });
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (typeFilter !== 'all') params.append('templateType', typeFilter);
      if (search) params.append('search', search);
      const { data } = await api.get(`/communications/templates?${params}`);
      setTemplates(data.items || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [channelFilter, typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (id) => {
    try { await api.post(`/communications/templates/${id}/archive`); toast.success('Archived'); load(); }
    catch { toast.error('Failed to archive'); }
  };
  const handleRestore = async (id) => {
    try { await api.post(`/communications/templates/${id}/restore`); toast.success('Restored'); load(); }
    catch { toast.error('Failed to restore'); }
  };
  const handleDuplicate = async (id, name) => {
    try { await api.post(`/communications/templates/${id}/duplicate?new_name=${encodeURIComponent(name + ' (Copy)')}`); toast.success('Duplicated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed to duplicate'); }
  };

  return (
    <div className="space-y-6" data-testid="communication-templates-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Communication Templates</h1>
        <Button data-testid="create-template-btn" onClick={() => { setEditTemplate(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-2" />New Template</Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Input data-testid="template-search" placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" />
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="w-32" data-testid="channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="grid gap-3">
          {templates.map(t => (
            <Card key={t.id} className={!t.active ? 'opacity-60' : ''}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  {t.channel === 'SMS' ? <MessageSquare className="w-5 h-5 text-blue-500" /> : <Mail className="w-5 h-5 text-purple-500" />}
                  <div>
                    <p className="font-medium">{t.name}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline">{t.channel}</Badge>
                      <Badge variant="secondary">{t.templateType?.replace(/_/g, ' ')}</Badge>
                      {!t.active && <Badge variant="destructive">Archived</Badge>}
                      {t.mergeFields?.length > 0 && <span className="text-xs text-muted-foreground">{t.mergeFields.length} merge fields</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`preview-template-${t.id}`} onClick={() => setPreviewId(t.id)}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`edit-template-${t.id}`} onClick={() => { setEditTemplate(t); setShowForm(true); }}><Edit2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDuplicate(t.id, t.name)}><Copy className="w-4 h-4" /></Button>
                  {t.active ? <Button size="sm" variant="ghost" onClick={() => handleArchive(t.id)}><Archive className="w-4 h-4" /></Button>
                    : <Button size="sm" variant="ghost" onClick={() => handleRestore(t.id)}><RotateCcw className="w-4 h-4" /></Button>}
                </div>
              </CardContent>
            </Card>
          ))}
          {templates.length === 0 && <p className="text-center text-muted-foreground py-8">No templates found</p>}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editTemplate?.id ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <TemplateForm template={editTemplate} onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      <PreviewDialog templateId={previewId} open={!!previewId} onClose={() => setPreviewId(null)} />
    </div>
  );
}
